import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Cookie, Loader2, Save } from 'lucide-react';
import api from '../../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';

const FIELDS = [
  ['privacy_label', 'Privacy link text'], ['privacy_url', 'Privacy link'],
  ['terms_label', 'Terms link text'], ['terms_url', 'Terms link'],
  ['accept_label', 'Accept button'], ['decline_label', 'Decline button'],
];

// Homepage Editor → Cookie notice: this academy's own notice, linked to its own legal documents.
export function CookieNoticeCard() {
  const [v, setV] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/homepage-config/admin/cookie-notice').then(({ data }) => setV(data)).catch(() => toast.error('Could not load the cookie notice'));
  }, []);
  if (!v) return null;
  const set = (patch) => setV((p) => ({ ...p, ...patch }));
  const save = async (next = v) => {
    setSaving(true);
    try { setV((await api.put('/homepage-config/admin/cookie-notice', next)).data); toast.success('Cookie notice saved'); }
    catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Card data-testid="cookie-notice-card">
      <CardHeader className="py-3">
        <div className="flex items-center gap-3">
          <Cookie className="h-4 w-4 text-slate-500" />
          <CardTitle className="text-base flex-1">Cookie notice</CardTitle>
          <Switch checked={v.enabled} onCheckedChange={(on) => set({ enabled: on })} data-testid="academy-cookie-enabled" />
        </div>
        <p className="text-xs text-slate-500">Off until you turn it on. Links point to your Privacy Policy and Terms from <a href="/admin/documents" className="underline">Legal Documents</a>.</p>
      </CardHeader>
      <CardContent className="border-t pt-4 space-y-3">
        <div>
          <Label className="text-xs text-slate-600">Message</Label>
          <Textarea rows={2} value={v.message} onChange={(e) => set({ message: e.target.value })} data-testid="academy-cookie-message" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs text-slate-600">{label}</Label>
              <Input value={v[k] || ''} onChange={(e) => set({ [k]: e.target.value })} className="h-8" data-testid={`academy-cookie-${k}`} />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button type="button" className="text-xs underline text-slate-600" onClick={() => save({ ...v, version: (v.version || 1) + 1 })} data-testid="academy-cookie-reshow">Save and ask every visitor again</button>
          <Button size="sm" onClick={() => save()} disabled={saving} data-testid="academy-cookie-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save notice
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
